/**
 * The section's slide deck (M45) — written here, presented on Present.
 *
 * Brian, 22 Sep: group the items by concept, give the room a lead-in that
 * teaches the point before the question, the push buttons at the foot of
 * that slide. M39 built that screen with a deck baked into the site; this is
 * where a coordinator writes one for any section. Each slide is a title, the
 * lead-in lines (one per line, revealed one at a time when presenting) and
 * which of the section's items are pushed from it. An item belongs to one
 * slide; items on no slide are listed so none is lost on the day.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { deckForClass } from '../decks/attestDemo.js';
import ErrorBanner from './ErrorBanner.jsx';

const blankSlide = () => ({ title: '', text: '', items: [], closing: false });
const fromDeck = (deck) => ({
  title: deck?.title ?? '',
  slides: (deck?.slides ?? []).map((s) => ({ key: s.key, title: s.title, text: (s.lines ?? []).join('\n'), items: s.items ?? [], closing: Boolean(s.closing) })),
});
const toDeck = (d) => ({
  title: d.title.trim() || null,
  slides: d.slides.map((s) => ({
    ...(s.key ? { key: s.key } : {}),
    title: s.title.trim(),
    lines: s.text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean),
    items: [...s.items].sort((a, b) => a - b),
    ...(s.closing ? { closing: true } : {}),
  })),
});

/** Items that are on no slide, and items on more than one. */
export function coverage(slides, positions) {
  const count = new Map(positions.map((p) => [p, 0]));
  for (const s of slides) for (const p of s.items) count.set(p, (count.get(p) ?? 0) + 1);
  return {
    missing: [...count].filter(([, n]) => n === 0).map(([p]) => p),
    doubled: [...count].filter(([, n]) => n > 1).map(([p]) => p),
  };
}

export function DeckEditor({ classId, canEdit }) {
  const [draft, setDraft] = useState(undefined); // undefined loading · null no deck · {title, slides}
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const builtIn = deckForClass(classId);

  const load = useCallback(async () => {
    try {
      const [deck, questions] = await Promise.all([
        apiFetch(`/api/v1/classes/${classId}/deck`),
        apiFetch(`/api/v1/classes/${classId}/questions`),
      ]);
      setItems((questions?.items ?? []).map((q, i) => ({ position: q.position ?? i + 1, stem: q.stem, type: q.type, check: q.is_completion_check })));
      setDraft(deck?.deck ? fromDeck(deck.deck) : null);
      setSaved(deck?.updated_at ?? null);
    } catch (err) {
      setError(err);
      setDraft(null);
    }
  }, [classId]);
  useEffect(() => {
    load();
  }, [load]);

  const positions = items.map((i) => i.position);
  const cov = draft ? coverage(draft.slides, positions) : { missing: positions, doubled: [] };
  const setSlide = (i, patch) => setDraft((d) => ({ ...d, slides: d.slides.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const move = (i, by) =>
    setDraft((d) => {
      const slides = [...d.slides];
      const [x] = slides.splice(i, 1);
      slides.splice(Math.max(0, Math.min(slides.length, i + by)), 0, x);
      return { ...d, slides };
    });

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/v1/classes/${classId}/deck`, { method: 'PUT', body: JSON.stringify(toDeck(draft)) });
      setDraft(fromDeck(r.deck));
      setSaved(r.updated_at);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Remove this deck? Present goes back to the plain list of items.')) return;
    setBusy(true);
    try {
      await apiFetch(`/api/v1/classes/${classId}/deck`, { method: 'DELETE' });
      setDraft(null);
      setSaved(null);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="deck-heading">
      <h2 id="deck-heading">Slide deck</h2>
      <p className="muted">
        What <strong>Present</strong> shows. Each slide teaches the point first — its lines reveal one at a time (tap, Space or →) —
        then the items you put on it are pushed from buttons at the foot, and the live answers appear at the top of the slide.
        Without a deck, Present shows the plain list of items.
      </p>
      <ErrorBanner error={error} />
      {draft === undefined && <p className="muted">Loading…</p>}

      {draft === null && (
        <>
          <p className="muted">
            {builtIn ? 'This section is presenting the deck built into the site for demo day. ' : 'No deck yet. '}
            {canEdit ? 'Start one:' : ''}
          </p>
          {canEdit && (
            <p className="toolbar">
              {builtIn && (
                <button type="button" className="btn btn--primary btn--auto" onClick={() => setDraft(fromDeck(builtIn))}>
                  Start from the demo-day deck
                </button>
              )}
              <button
                type="button"
                className="btn btn--auto"
                disabled={!items.length}
                onClick={() => setDraft({ title: '', slides: items.map((i) => ({ ...blankSlide(), title: '', items: [i.position] })) })}
              >
                One slide per item
              </button>
              <button type="button" className="btn btn--quiet btn--auto" onClick={() => setDraft({ title: '', slides: [blankSlide()] })}>
                Blank deck
              </button>
            </p>
          )}
        </>
      )}

      {draft && (
        <>
          <label className="field">
            <span>Deck title (shown above the slides)</span>
            <input type="text" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} disabled={!canEdit || busy} maxLength={160} />
          </label>

          {draft.slides.map((s, i) => (
            <fieldset key={s.key ?? `new-${i}`} className="panel panel--quiet deck-slide">
              <legend>
                Slide {i + 1}
                {s.closing ? ' · closing' : ''}
              </legend>
              <label className="field">
                <span>Title</span>
                <input type="text" value={s.title} onChange={(e) => setSlide(i, { title: e.target.value })} disabled={!canEdit || busy} maxLength={160} />
              </label>
              <label className="field">
                <span>Lead-in — one point per line, revealed in order</span>
                <textarea rows={Math.max(3, s.text.split('\n').length + 1)} value={s.text} onChange={(e) => setSlide(i, { text: e.target.value })} disabled={!canEdit || busy} />
              </label>
              {items.length > 0 && (
                <div className="field">
                  <span>Items pushed from this slide</span>
                  {items.map((it) => {
                    const elsewhere = draft.slides.findIndex((x, j) => j !== i && x.items.includes(it.position));
                    return (
                      <label key={it.position} className="checkbox">
                        <input
                          type="checkbox"
                          checked={s.items.includes(it.position)}
                          disabled={!canEdit || busy}
                          onChange={(e) =>
                            setSlide(i, { items: e.target.checked ? [...s.items, it.position] : s.items.filter((p) => p !== it.position) })
                          }
                        />
                        <span>
                          {it.position}. {it.stem}
                          {it.check ? <span className="badge"> final</span> : null}
                          {elsewhere >= 0 && !s.items.includes(it.position) ? <span className="muted"> — on slide {elsewhere + 1}</span> : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {canEdit && (
                <p className="toolbar">
                  <label className="checkbox">
                    <input type="checkbox" checked={s.closing} onChange={(e) => setSlide(i, { closing: e.target.checked })} disabled={busy} />
                    <span>Closing slide</span>
                  </label>
                  <button type="button" className="btn btn--quiet btn--auto" onClick={() => move(i, -1)} disabled={busy || i === 0}>
                    ↑ Up
                  </button>
                  <button type="button" className="btn btn--quiet btn--auto" onClick={() => move(i, 1)} disabled={busy || i === draft.slides.length - 1}>
                    ↓ Down
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet btn--auto"
                    onClick={() => setDraft((d) => ({ ...d, slides: d.slides.filter((_, j) => j !== i) }))}
                    disabled={busy || draft.slides.length === 1}
                  >
                    Remove slide
                  </button>
                </p>
              )}
            </fieldset>
          ))}

          {cov.missing.length > 0 && (
            <p className="callout callout--warn">
              <strong>
                {cov.missing.length === 1 ? 'Item' : 'Items'} {cov.missing.join(', ')} {cov.missing.length === 1 ? 'is' : 'are'} on no slide.
              </strong>
              <span className="callout__detail">It could not be pushed while presenting. Put it on a slide, or leave it on purpose.</span>
            </p>
          )}
          {cov.doubled.length > 0 && (
            <p className="callout callout--warn">
              <strong>Items {cov.doubled.join(', ')} are on more than one slide.</strong>
              <span className="callout__detail">One slide per item — the deck will not save until each is on one.</span>
            </p>
          )}

          {canEdit && (
            <p className="toolbar">
              <button type="button" className="btn btn--auto" onClick={() => setDraft((d) => ({ ...d, slides: [...d.slides, blankSlide()] }))} disabled={busy}>
                Add a slide
              </button>
              <button
                type="button"
                className="btn btn--primary btn--auto"
                onClick={save}
                disabled={busy || cov.doubled.length > 0 || draft.slides.some((s) => !s.title.trim())}
              >
                {busy ? 'Saving…' : 'Save the deck'}
              </button>
              {saved && (
                <button type="button" className="btn btn--quiet btn--auto" onClick={remove} disabled={busy}>
                  Remove the deck
                </button>
              )}
              <span className="muted">{saved ? `Saved ${new Date(saved).toLocaleString()}` : 'Not saved yet'}</span>
            </p>
          )}
        </>
      )}
    </section>
  );
}

export default DeckEditor;
